import { existsSync, writeFileSync } from 'fs';
import { join } from 'path';

// Companion to the WayAI hub-folder memory seeding (#618): a combined WayAI+Rekor repo should give
// coding agents base-context memory in the same shape it finds hub-context memory. Everything here is
// strictly create-if-absent — we NEVER overwrite a file the user or an agent already authored.
// Scope is the base folder only: we no longer seed a repo-root bootstrap (that top-level file was
// too intrusive for the user's repo).

/** Base-folder `AGENTS.md` seed: a prompt for the agent to record what the base's config can't capture. */
const BASE_AGENTS_MD = `# Base context

<!-- Seeded by \`rekor pull\`/\`push\`. Safe to edit — Rekor never overwrites this file. -->

This file is the durable, agent-facing memory for **this base**. The base's settings and config
(record types, relationships, triggers, toolsets) describe *what* exists; record here the context
they can't capture — the reasoning an agent needs before touching this data layer:

- **Purpose** — what this base models and who/what depends on it.
- **Key decisions & why** — schema shapes, archival modes, integration choices, and the trade-offs behind them.
- **Business rules** — invariants and constraints that live in the domain, not the JSON Schema.
- **Terminology** — domain terms and how they map to record types / fields.
- **Integration quirks** — external-source behaviors, webhook expectations, gotchas.

Keep it current as the base evolves.
`;

/** One-line `CLAUDE.md` import shim so Claude Code loads the shared `AGENTS.md` memory. */
const BASE_CLAUDE_MD = `@AGENTS.md\n`;

/** Write `contents` to `path` only if the file does not already exist. Returns true if it wrote. */
function writeIfAbsent(path: string, contents: string): boolean {
  if (existsSync(path)) return false;
  writeFileSync(path, contents);
  return true;
}

/**
 * Seed a base folder's agent-memory files (create-if-absent): `AGENTS.md` (the base-context memory)
 * plus a one-line `CLAUDE.md` `@AGENTS.md` import shim. Never overwrites existing files, so a
 * user/agent-authored memory always survives a subsequent `pull`/`push`. Callers must not point this
 * at a read-only production mirror folder — the mirror's `.gitignore` of `*` would ignore the seed
 * anyway; `pull` returns before its seed call for a direct-production target.
 */
export function seedBaseMemoryFiles(folder: string): void {
  writeIfAbsent(join(folder, 'AGENTS.md'), BASE_AGENTS_MD);
  writeIfAbsent(join(folder, 'CLAUDE.md'), BASE_CLAUDE_MD);
}
